import type { PayloadAction, Slice } from "@reduxjs/toolkit";
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { type SosEventResponse } from "@somewear/api";
import { emitAssetAccountsDeleted } from "@somewear/asset";
import type { IError } from "@somewear/model";
import { clearWorkspace } from "@somewear/workspace";

import { resolveSosSessions } from "./sos.actions";
import type { SosState } from "./sos.state";
import { shouldUpsertSosEvent } from "./sos.utils";

const adapter = createEntityAdapter<SosEventResponse.AsObject, string>({
	selectId: (entity) => entity.sessionId,
});

// Rename the exports for readability in component usage
export const {
	selectAll: selectAllSosEvents,
	selectById: selectSosEventById,
	selectIds: selectAllSosEventSessionIds,
} = adapter.getSelectors((state: SosState) => state.sosEvents);

const sosSlice: Slice = createSlice({
	name: "sos",
	initialState: adapter.getInitialState({ isResolvePending: false }),
	reducers: {
		apiSosEventsRequest() {},
		apiSosEventsError(state, action: PayloadAction<IError>) {},
		apiSosEventsSuccess(state, action: PayloadAction<SosEventResponse.AsObject[]>) {
			action.payload.forEach((event) => {
				const prevEvent = adapter.getSelectors().selectById(state, event.sessionId);
				if (shouldUpsertSosEvent(event, prevEvent)) {
					adapter.upsertOne(state, event);
				}
			});
		},
	},
	extraReducers: (builder) => {
		builder.addCase(resolveSosSessions.pending, (state) => {
			state.isResolvePending = true;
		});
		builder.addCase(resolveSosSessions.fulfilled, (state, action) => {
			state.isResolvePending = false;
			action.payload.data.forEach((event) => {
				const prevEvent = adapter.getSelectors().selectById(state, event.sessionId);
				if (shouldUpsertSosEvent(event, prevEvent)) {
					adapter.upsertOne(state, event);
				}
			});
		});
		builder.addCase(resolveSosSessions.rejected, (state) => {
			state.isResolvePending = false;
		});
		builder.addCase(clearWorkspace, (state, action) => {
			const deletedItems = adapter
				.getSelectors()
				.selectAll(state)
				.filter((it) => it.workspaceId === action.payload);
			adapter.removeMany(
				state,
				deletedItems.map((event) => event.sessionId),
			);
		});
		builder.addCase(emitAssetAccountsDeleted, (state, action) => {
			// when an account is deleted, delete the related data
			const deletedAccountIds = action.payload.map((it) => it.id);
			const deletedItems = adapter
				.getSelectors()
				.selectAll(state)
				.filter((it) => deletedAccountIds.includes(it.userId));
			adapter.removeMany(
				state,
				deletedItems.map((event) => event.sessionId),
			);
		});
	},
});

export const { apiSosEventsRequest, apiSosEventsError, apiSosEventsSuccess } = sosSlice.actions;

export default sosSlice;
